import { useEffect, useRef, useState } from "react";

/**
 * Logo "trame ·" with two animation variants for the dot:
 *  - magnetic: dot follows the cursor within a small radius
 *  - wiggle: dot oscillates left-right on hover
 */
export default function Logo({ variant = "wiggle", size = "text-2xl", onClick, dataTestid = "logo", label = "tramæ" }) {
  const wrapRef = useRef(null);
  const dotRef = useRef(null);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    if (variant !== "magnetic") return;
    const wrap = wrapRef.current;
    const dot = dotRef.current;
    if (!wrap || !dot) return;

    const onMove = (e) => {
      const rect = wrap.getBoundingClientRect();
      const cx = rect.left + rect.width - 14;
      const cy = rect.top + rect.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.hypot(dx, dy);
      const max = 6;
      const k = Math.min(dist, 60) / 60;
      const tx = (dx / (dist || 1)) * max * k;
      const ty = (dy / (dist || 1)) * max * k;
      dot.style.transform = `translate(${tx}px, ${ty}px)`;
    };
    const onLeave = () => {
      dot.style.transform = "translate(0,0)";
    };
    wrap.addEventListener("mousemove", onMove);
    wrap.addEventListener("mouseleave", onLeave);
    return () => {
      wrap.removeEventListener("mousemove", onMove);
      wrap.removeEventListener("mouseleave", onLeave);
    };
  }, [variant]);

  return (
    <button
      ref={wrapRef}
      type="button"
      data-testid={dataTestid}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="relative inline-flex items-baseline gap-2 group cursor-pointer select-none"
      aria-label={`${label} — torna su`}
    >
      <span
        className={`font-display ${size} tracking-[0.32em] lowercase text-[color:var(--tr-ink)] leading-none`}
        style={{ color: "#1A1918" }}
      >
        {label.split(/(æ)/i).map((part, i) =>
          /^æ$/i.test(part) ? (
            <span key={i} data-testid="logo-ae" className="tr-logo-ae">{part}</span>
          ) : (
            part
          )
        )}
      </span>
      <span
        ref={dotRef}
        data-testid="logo-dot"
        className={`relative bottom-[1.75px] inline-block w-[5.5px] h-[5.5px] rounded-full ${
          variant === "magnetic"
            ? "transition-transform duration-300 ease-out"
            : hover
            ? "tr-dot-wiggle"
            : ""
        }`}
        style={{ backgroundColor: "#A08062" }}
      />
    </button>
  );
}
